import { Control } from 'ol/control';
import isBase64 from "../function/legend";
import GIS from "../app";
import {LayersOptions} from "../configuration/control";
import {Icon, LayerType, Legend, Symbol as SymbolConfiguration} from "../configuration/layer";
import LayerGroup from "ol/layer/Group";
import {Collection} from "ol";
import {Emitter} from "../base/emitter";

interface HTMLGroup {
    groupDiv: HTMLDivElement;
    groupHeader: HTMLSpanElement;
}

const defaultOptions = {
    title: 'Legend',
    tip: 'Legend',
    hideTip: 'Hide panel'
}

class LegendControl extends Control {
    private readonly GIS;
    private readonly showButton;
    private readonly legend;
    private isShown = false;
    private emitter: Emitter;

    constructor(GIS: GIS, emitter: Emitter, options: LayersOptions = defaultOptions) {
        const element = document.createElement('div');
        super({
            element: element,
            target: options && options.target,
        });

        this.GIS = GIS;
        this.emitter = emitter;

        this.showButton = document.createElement('button');
        this.showButton.innerHTML = '<i class="fal fa-list"/>';
        this.showButton.id = `cfm-legend-button-${this.GIS.getInstanceCounter()}`;
        this.GIS.Localisation.bindTitle(
            this.showButton,
            options?.i18n?.tip,
            options?.tip ?? 'Legend'
        );
        this.showButton.type = 'button';

        this.legend = document.createElement('div');

        element.className = 'cfm-tool cfm-legend ol-unselectable ol-control';
        element.appendChild(this.showButton);

        if (options) {
            this.createLegend(options);
        }
        this.showButton.addEventListener('click', () => this.togglePanel(), false);

        this.emitter.on('legendHide', this.hidePanel.bind(this));
        this.emitter.on('legendShow', this.showPanel.bind(this));
    }

    private hidePanel() {
        this.isShown = false;
        this.legend.className = 'cfm-panel hidden';
        this.emitter.emit('sidebarHide');
    }

    private showPanel () {
        this.isShown = true;
        this.legend.className = 'cfm-panel visible';
        this.emitter.emit('layersHide');
        this.emitter.emit('sidebarShow');
    }

    private togglePanel() {
        this.isShown ? this.hidePanel() : this.showPanel();
        this.isShown = !this.isShown;
    }

    private createLayerLegend(name:string, htmlGroup:HTMLGroup, visible:boolean) {

        const layerDiv = document.createElement('div');
        layerDiv.id = `cfm-layer-legend-${name}-${this.GIS.getInstanceCounter()}`;
        layerDiv.style.display = visible ? '' : 'none';
        htmlGroup.groupDiv.appendChild(layerDiv);

        return layerDiv;
    }

    private createGroup(group:LayerGroup) {
        const groupName = group.get('name');
        const collapsible = group.get('collapsible');

        const groupHeader = document.createElement('span');
        groupHeader.className = 'cfm-group-header';

        const groupDiv = document.createElement('div');
        groupDiv.id = `group-legend-div-${groupName}-${this.GIS.getInstanceCounter()}`;
        groupDiv.className = 'cfm-group-layer cfm-visible';

        if (collapsible !== false) {
            const groupCloseButton = document.createElement('button');
            groupCloseButton.className = 'cfm-group-close'
            groupCloseButton.innerHTML = '<i class="fal fa-angle-right"/>';
            groupCloseButton.type = 'button';

            groupHeader.appendChild(groupCloseButton);

            const groupTitle = document.createElement('span');
            groupTitle.className = 'cfm-group-title';
            this.GIS.Localisation.bindLazy(
                t => { groupTitle.innerText = t; },
                () => (group.get('i18n') as { title?: Record<string, string> } | undefined)?.title,
                group.get('title')
            );

            groupHeader.appendChild(groupTitle);

            const collapseGroup = () => {
                groupDiv.classList.toggle("cfm-visible");
                if (groupDiv.classList.contains('cfm-visible')) {
                    groupCloseButton.innerHTML = '<i class="fal fa-angle-right"/>';
                } else {
                    groupCloseButton.innerHTML = '<i class="fal fa-angle-down"/>';
                }
            }

            groupCloseButton.addEventListener('click', collapseGroup, false);
            groupTitle.addEventListener('click', collapseGroup, false);
        }

        return { groupDiv: groupDiv, groupHeader: groupHeader }
    };

    private createLegendHeader(
        layerDiv: HTMLDivElement,
        title: string,
        i18n?: Record<string, string>
    ) {
        const layerTitleSpan = document.createElement('span');
        layerTitleSpan.className = 'cfm-layer-header';
        this.GIS.Localisation.bindHTML(layerTitleSpan, i18n, title);

        layerDiv.appendChild(layerTitleSpan);
    }

    private createLegendEntry(layerDiv:HTMLDivElement, title:string, icon:SVGElement | HTMLElement) {
        const layerSpan = document.createElement('span');
        layerSpan.className = 'cfm-layer-entry';
        const layerTitle = document.createElement('span');
        layerTitle.className = 'cfm-layer-entry-title'
        layerTitle.innerHTML = title;


        layerSpan.appendChild(icon)
        layerSpan.appendChild(layerTitle);
        layerDiv.appendChild(layerSpan);
    }

    private createRampLegendEntry(layerDiv:HTMLDivElement, icon:SVGElement | HTMLElement) {
        const layerSpan = document.createElement('div');
        layerSpan.className = 'cfm-layer-entry';

        layerSpan.appendChild(icon);

        layerDiv.appendChild(layerSpan);
    }

    private createIconImage(config: SymbolConfiguration & Icon) {
        const img = document.createElement('img');
        if (config.imageData && isBase64(config.imageData)) {
            img.id = config.url;
            img.src = "data:" + config.contentType + ";base64," + config.imageData;
            img.style.height = config.height + 'px';
            img.style.width = config.width + 'px';
        } else if (config.icon) {
            img.src = config.icon;
            img.style.height = config.height + 'px';
            img.style.width = config.width + 'px';
        }

        return img;
    }

    private createLegend (options: LayersOptions) {
        this.legend.id = `cfm-legend-${this.GIS.getInstanceCounter()}`;
        this.legend.className = 'cfm-panel hidden';

        const legendHeader = document.createElement('div');
        legendHeader.className = 'cfm-panel-header';

        const legendTitle = document.createElement('h1');
        this.GIS.Localisation.bindHTML(
            legendTitle,
            options?.i18n?.title,
            options?.title ?? 'Legend'
        );

        const legendIcon = document.createElement('i')
        legendIcon.innerHTML = '<i class="fal fa-list"/>';

        const closeButton = document.createElement('button');
        closeButton.innerHTML = '<i class="fal fa-times"/>';
        this.GIS.Localisation.bindTitle(
            closeButton,
            options?.i18n?.hideTip,
            options?.hideTip ?? 'Hide panel'
        );
        closeButton.className = 'cfm-panel-close';
        closeButton.type = 'button';

        legendHeader.appendChild(legendIcon);
        legendHeader.appendChild(legendTitle);
        legendHeader.appendChild(closeButton);
        this.legend.appendChild(legendHeader);

        const legendContent = document.createElement('div');
        legendContent.setAttribute('class', 'cfm-panel-content');

        this.GIS.Layer.groupEntries.map(group => {
            let htmlGroup:HTMLGroup;

            if (group.get("title")) {
                htmlGroup = this.createGroup(group);

                legendContent.appendChild(htmlGroup.groupHeader);
                legendContent.appendChild(htmlGroup.groupDiv);
            }

            const layers = group.getLayers() as Collection<LayerType & Legend>;
            layers.forEach((layer) => {
                const symbology = layer.get('symbology');
                const switcher = layer.get('switcher');
                let layerDiv:HTMLDivElement;

                if (symbology && symbology.styled) {
                    const layerConfig = this.GIS.Style.getLayerConfig(symbology.name) as SymbolConfiguration & Icon;
                    layerDiv = this.createLayerLegend(layer.get('name'), htmlGroup, layer.getVisible());

                    switch (symbology.type) {
                        case 'unique':
                            let icon = this.GIS.Style.createUniqueSVG(symbology.geometry, layerConfig, undefined, undefined)
                            this.createLegendHeader(layerDiv, switcher.title, switcher.i18n?.title);
                            if (icon instanceof SVGElement || icon instanceof HTMLElement) {
                                this.createLegendEntry(layerDiv, symbology.title, icon);
                            }

                            break;
                    }
                } else if (symbology) {
                    let icon: SVGElement | HTMLImageElement | Element | string;
                    layerDiv = this.createLayerLegend(layer.get('name'), htmlGroup, layer.getVisible());

                    switch (symbology.type) {

                        case 'unique':
                            if (symbology.symbol.icon) {
                                icon = this.createIconImage(symbology.symbol);
                            } else {
                                icon = this.GIS.Style.createUniqueSVG(symbology.geometry, symbology.symbol, undefined, undefined);
                            }
                            this.createLegendHeader(layerDiv, switcher.title, switcher.i18n?.title);
                            if (icon instanceof SVGElement || icon instanceof HTMLElement) {
                                this.createLegendEntry(layerDiv, symbology.symbol.title, icon);
                            }

                            break;
                        case 'category':
                            this.createLegendHeader(layerDiv, switcher.title, switcher.i18n?.title);

                            symbology.entries.map((symbol:SymbolConfiguration & Icon) => {
                                if (symbol.icon || symbol.imageData) {
                                    icon = this.createIconImage(symbol);
                                } else {
                                    symbol.geometry && (icon = this.GIS.Style.createUniqueSVG(symbol.geometry, symbol, undefined, undefined));
                                }

                                if (icon instanceof SVGElement || icon instanceof HTMLElement) {
                                    this.createLegendEntry(layerDiv, symbol.title, icon);
                                }
                            });
                            break;
                        case 'ramp':
                            this.createLegendHeader(layerDiv, switcher.title, switcher.i18n?.title);
                            icon = this.GIS.Style.createContinuousSVG(symbology.steps, undefined, undefined);
                            if (icon instanceof SVGElement || icon instanceof HTMLElement) {
                                this.createRampLegendEntry(layerDiv, icon);
                            }
                            break;
                    }
                }

                if (symbology || group.get("groupLayer") === (layer.get("name"))) {

                    const hideLegend = () => {
                        if (group.get("groupLayer") !== undefined) {
                            group.getLayers().forEach(layer => {

                                if (group.get("groupLayer") !== layer.get("name") && layer.get('symbology')) {

                                    const divName = "#cfm-layer-legend-" + (layer.get("name"));
                                    const htmlDiv = document.querySelector(divName);
                                    (htmlDiv as HTMLElement).style.display = 'none';
                                }
                            });
                        } else { layerDiv.style.display = 'none'; }
                    };

                    const showLegend = () => {
                        if (group.get("groupLayer") !== undefined) {
                            group.getLayers().forEach(layer => {

                                if (group.get("groupLayer") !== layer.get("name") && layer.get('symbology')) {

                                    const divName = "#cfm-layer-legend-" + (layer.get("name"));
                                    const htmlDiv = document.querySelector(divName);
                                    (htmlDiv as HTMLElement).style.display = '';

                                }
                            });
                        } else { layerDiv.style.display = ''; }
                    };

                    layer.legend = true;
                    layer.hideLegend = hideLegend;
                    layer.showLegend = showLegend;
                }

            });
        });

        this.legend.appendChild(legendContent);

        const targetElement = this.GIS.Map.olObject!.getTargetElement();
        targetElement.appendChild(this.legend);

        closeButton.addEventListener('click', () => this.hidePanel(), false);
    };
}

export default LegendControl;
